import type React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import calendar from '@/assets/icon/calendar.png';
import searchIcon from '@/assets/icon/icon-search.svg';
import { cn } from '@/lib/utils';
import { Input } from '@/shared/components/ui/input';
import type { GeneralInputProps } from './GeneralInput.types';

export interface GeneralInputRef {
  focus: () => void;
  empty: () => void;
}

const GeneralInput = forwardRef<GeneralInputRef, GeneralInputProps>(
  (
    {
      customInput,
      inputFormStyle,
      type,
      placeholder,
      readonly,
      value,
      id,
      name,
      maxLength,
      pattern = '',
      rightIconStyle,
      rightIcon,
      disabled,
      isDob,
      isSearch,
      required,
      onKeyUp = () => {},
      onChange = () => {},
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const [inputValue, setInputValue] = useState<string | number>(value || '');

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      if (pattern) {
        const regex = new RegExp(pattern);
        if (regex.test(value)) {
          setInputValue(value);
        } else {
          return;
        }
      } else {
        setInputValue(value);
      }

      onChange(event);
    };

    useEffect(() => {
      const val = value || '';
      setInputValue(val);
    }, [value]);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      empty: () => setInputValue(''),
    }));

    if (isDob) {
      return (
        <div className="relative w-full">
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            className={cn('input pl-4 pr-10 w-full', inputFormStyle)}
            value={inputValue}
            id={id}
            maxLength={maxLength}
            disabled={disabled}
            onKeyUp={onKeyUp}
            name={name}
            readOnly={readonly}
            required={required}
            onChange={handleInputChange}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.focus()}
            aria-label="Open calendar"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
          >
            <img src={calendar} alt="" aria-hidden="true" />
          </button>
        </div>
      );
    }

    if (isSearch) {
      return (
        <div className={cn('relative', customInput)}>
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            className={cn('input pl-4 pr-10 w-full', inputFormStyle)}
            value={inputValue}
            id={id}
            maxLength={maxLength}
            disabled={disabled}
            onKeyUp={onKeyUp}
            name={name}
            required={required}
            onChange={handleInputChange}
          />
          <img
            src={searchIcon}
            alt=""
            aria-hidden="true"
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
          />
        </div>
      );
    }

    return (
      <div className={cn(customInput, 'relative')}>
        <Input
          ref={inputRef}
          type={type}
          placeholder={placeholder}
          value={inputValue}
          onKeyUp={onKeyUp}
          onChange={handleInputChange}
          className={cn('input', inputFormStyle)}
          maxLength={maxLength}
          id={id}
          pattern={pattern}
          name={name}
          readOnly={readonly}
          disabled={disabled}
          required={required}
        />
        {rightIcon && <img src={rightIcon} alt="icon" className={rightIconStyle} />}
      </div>
    );
  }
);

export default GeneralInput;
